import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'

import FulfillmentPanel from 'src/public/components/default_template/online_ordering/dining_behavior_toggle/FulfillmentPanel'
import {
  DisplayableMenuItemTag,
  HeroContentType,
  MenuFormatConfig
} from 'src/apollo/sites'
import { useTracker } from 'src/lib/js/hooks/useTracker'
import { getReferrerProperties } from 'src/lib/js/trackerUtilities'
import ToastProduct from 'src/public/components/online_ordering/ToastProduct'
import { Channel, isWebsiteNonOrderRoute } from 'src/public/js/siteUtilities'
import { useNavHistory } from 'src/shared/components/common/history_context/NavHistoryContext'
import { useTrackOrderPage } from 'src/shared/js/hooks/useTrackOrderPage'
import { getDisplayBusinessName } from 'src/shared/js/locationUtils'
import { normalizeHtmlId } from 'src/shared/js/normalizeUtils'
import { t } from 'src/translations'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'

import { NoAvailableMenuItems } from 'public/components/default_template/no_available_menu_items/NoAvailableMenuItems'
import OrderPageSchema from 'public/components/default_template/OrderPageSchema'
import MenuPageHeader from 'public/components/default_template/menu_header/MenuPageHeader'
import MenuNav, {
  useMenuNav
} from 'public/components/default_template/menu_nav/MenuNav'
import { scrollToRef } from 'public/components/default_template/menu_nav/menuNavScrollUtils'
import { getTemplateClass } from 'public/components/default_template/menu_section/MenuItemCard'
import MenuSection, {
  MenuItem
} from 'public/components/default_template/menu_section/MenuSection'
import FulfillmentTimeModal from 'public/components/default_template/online_ordering/dining_behavior_toggle/FulfillmentTimeModal'
import DeeplinkItemModal from 'public/components/default_template/online_ordering/item_modal/DeeplinkItemModal'
import { ItemResolutionModal } from 'public/components/default_template/online_ordering/item_modal/ItemResolutionModal'
import {
  filterAndSortTags,
  OfferBadge
} from 'public/components/default_template/online_ordering/item_tags/MenuItemTags'
import StickyOrderFooter from 'public/components/default_template/online_ordering/order/StickyOrderFooter'
import ReorderItems from 'public/components/default_template/online_ordering/reorder_items/ReorderItems'
import { useMenuSearchContext } from 'public/components/default_template/search'
import { useLocationSchedules } from 'public/components/online_ordering/FulfillmentContext'
import { useOffers } from 'public/components/online_ordering/OffersContext'
import { useOOMenus } from 'public/components/online_ordering/useOOMenu'
import { useVariableMenuSpacing } from 'public/components/default_template/useVariableMenuSpacing'

import { MenuHeader } from './MenuHeader'
import Offers from './offers/Offers'
import OrderPageWrapper, {
  OrderPageContentProps
} from './online_ordering/pages/OrderPageWrapper'
import { FilterNav } from './paginated_menu/FilterNav'
import { FilterTags } from './paginated_menu/FilterTags'
import { PaginatedMenu } from './paginated_menu/PaginatedMenu'
import { CreditCardSurchargeDisclaimer } from 'public/components/default_template/surcharges/CreditCardSurchargeDisclaimer'
import { useCart } from 'public/components/online_ordering/CartContext'
import DeliveryModal from './online_ordering/delivery_address_modal/DeliveryModal'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import UpsellModal from './online_ordering/upsell/upsell_modal/UpsellModal'
import { ItemSource } from './online_ordering/cart/cartUtils'
import QRCodeLandingModal from './online_ordering/qr_code/QRCodeLandingModal'

export const OrderPageContent = ({
  scrollNavRef,
  hasHero,
  hideHero,
  usingSecondaryNav,
  menuGuid,
  groupGuid,
  itemGuid,
  hideToastLocalFulfillmentPanel
}: OrderPageContentProps) => {
  const {
    restaurant: siteRestaurant,
    selectedLocation,
    ooRestaurant: restaurant,
    toastProduct
  } = useRestaurant()
  const { menuOffers } = useOffers()
  const [isMapOrModalOpen, setIsMapOrModalOpen] = useState(false)
  const hasScrolledToParam = useRef(false)

  const navRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)
  const ooConfig = useMemo(
    () => siteRestaurant.config.ooConfig,
    [siteRestaurant.config.ooConfig]
  )
  const displayableMenuItemTags =
    ooConfig?.displayableMenuItemTags as DisplayableMenuItemTag[]
  const { deliveryModalOpen, setDeliveryModalOpen, deliveryModalForcedScreen } =
    useDelivery()
  const isToastLocal = useMemo(
    () =>
      toastProduct === ToastProduct.ToastLocal ||
      toastProduct === ToastProduct.FreeOOToastLocal ||
      toastProduct === ToastProduct.ToastApp,
    [toastProduct]
  )

  const { isSearching } = useMenuSearchContext()

  const { isEditor, useEditableRef } = useEditor()
  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'menu',
    displayName: 'Menu',
    actions: [],
    schema: { fields: [] },
    skipRef: navRef
  })

  const restaurantGuid = selectedLocation.externalId
  const {
    menus,
    popularItems: unfilteredPopularItems,
    menuItems,
    groupings,
    filters,
    setFilters,
    fetchMore,
    loadingNewFilterData,
    loading
  } = useOOMenus({ respectAvailability: !isEditor })
  const variableMenuSpacing = useVariableMenuSpacing()
  const {
    selectedMenuIndex,
    setSelectedMenuIndex,
    scrolledMenuIndex,
    setScrolledMenuIndex,
    selectedGuid,
    setSelectedGuid,
    scrolledGuid,
    setScrolledGuid,
    refMenuPages,
    itemGuidsToItemsMap
  } = useMenuNav({ menus, menuItems, page: '/order' })

  const deeplinkItem = itemGuid ? itemGuidsToItemsMap.get(itemGuid) : null

  // Collect all menu item tags to build comprehensive GUID->name mapping
  // Critical for RF-32427: enables name-based matching for duplicate tag records
  const allMenuItemTags = useMemo(
    () => menuItems?.flatMap((item) => item.itemTags ?? []) ?? [],
    [menuItems]
  )

  const popularItems = useMemo<MenuItem[]>(() => {
    const itemsWithFilteredTags =
      unfilteredPopularItems?.map((item) => ({
        ...item,
        itemTags: filterAndSortTags(
          item?.itemTags ?? [],
          displayableMenuItemTags,
          allMenuItemTags
        )
      })) ?? []
    return itemsWithFilteredTags
  }, [unfilteredPopularItems, displayableMenuItemTags, allMenuItemTags])

  const shouldShowMenuTitles = refMenuPages.length > 1

  // Toast Local ordering can be enabled via the OO product module, or the Toast Local product module
  const hasOOEnabled = useMemo(
    () =>
      isToastLocal
        ? restaurant?.hasToastLocalModule || restaurant?.hasOnlineOrderingModule
        : restaurant?.hasOnlineOrderingModule,
    [isToastLocal, restaurant]
  )

  const pageNode = useCallback(
    (node: any) => {
      if (node && menuGuid && !hasScrolledToParam.current) {
        const menuIndex = refMenuPages?.findIndex(
          (menu) => menu.guid === menuGuid
        )
        const pageRef = refMenuPages[menuIndex]?.ref
        if (groupGuid) {
          const groupRef = refMenuPages[menuIndex]?.groups.find(
            (group) => group.guid === groupGuid
          )?.ref
          if (groupRef) {
            setSelectedMenuIndex(menuIndex)
            scrollToRef(groupRef)
            hasScrolledToParam.current = true
          }
          return
        }
        if (menuIndex && pageRef && menuIndex !== selectedMenuIndex) {
          setSelectedMenuIndex(menuIndex)
          scrollToRef(pageRef)
          hasScrolledToParam.current = true
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refMenuPages]
  )

  const ooFormatConfig: MenuFormatConfig | undefined | null = ooConfig?.format

  // When the business and location names match, collapse to a single copy;
  // every other name renders unchanged.
  const heading = useMemo(
    () =>
      getDisplayBusinessName(
        restaurant?.whiteLabelName,
        selectedLocation?.name
      ) || 'Menu',
    [restaurant, selectedLocation]
  )

  const locationSchedules = useLocationSchedules()
  const locationSchedule =
    (locationSchedules && locationSchedules[selectedLocation.externalId]) ||
    undefined
  const showFeaturedItems =
    popularItems.length > 0 &&
    (selectedLocation.featuredItems?.enabled ||
      (selectedLocation.featuredItems?.enabled === undefined &&
        !ooConfig?.popularItemsDisabled))

  const { surcharges } = useCart()

  // Check if surcharging is enabled based on surchargingConfig
  const surchargingConfig = restaurant?.surchargingConfig
  // Only show if enabled flag is true (not cart dependent)
  const shouldShowSurchargeDisclaimer = surchargingConfig?.enabled === true

  return (
    <>
      {!hideHero && (
        <MenuPageHeader
          heading={heading}
          heroConfig={ooConfig as HeroContentType}
          allowFulfillmentChange
        />
      )}
      <div
        className='pageContent'
        ref={scrollNavRef}
        data-testid='order-page'
        role='main'
        id='main'
        aria-label={t('order-page.menu')}
      >
        {!hasOOEnabled ? (
          <div className='onlineOrderingUnavailable'>
            {t('order-page.ordering-unavailable')}
          </div>
        ) : (
          <div ref={editableRef}>
            <div className='navAndMenu'>
              {menus ? (
                <MenuNav
                  menus={refMenuPages}
                  selectedGroupGuid={selectedGuid}
                  setSelectedGroupGuid={setSelectedGuid}
                  selectedMenuIndex={selectedMenuIndex}
                  setSelectedMenuIndex={setSelectedMenuIndex}
                  menuConfig={ooFormatConfig}
                  navRef={navRef}
                  className={classnames({
                    withoutHero: !hasHero,
                    withShadow: usingSecondaryNav
                  })}
                />
              ) : null}
              {menuItems?.length && groupings ? (
                <FilterNav
                  groupings={groupings}
                  filters={filters}
                  setFilters={setFilters}
                  alignment='left'
                />
              ) : null}
              {isToastLocal && !hideToastLocalFulfillmentPanel && (
                <div
                  className='paddedContentWrapper'
                  data-testid='toast-local-fulfillment-panel'
                >
                  <div className='content paddedContent'>
                    <FulfillmentPanel />
                  </div>
                </div>
              )}
              <div className='menuSections'>
                {menuItems?.length && groupings ? (
                  <FilterTags
                    groupings={groupings}
                    filters={filters}
                    setFilters={setFilters}
                  />
                ) : null}
                <div className='promotionalSections'>
                  {!isSearching && <Offers />}
                  {isEditor ? null : <ReorderItems />}
                  {showFeaturedItems && !menuItems?.length ? (
                    <div
                      className={classnames(
                        'menuSectionWrapper',
                        getTemplateClass(ooFormatConfig?.template),
                        { columns: ooFormatConfig?.columns }
                      )}
                    >
                      <div className='paddedContentWrapper'>
                        <div className='menu paddedContent featuredItems'>
                          <MenuSection
                            restaurantGuid={restaurantGuid}
                            setIsMapOrModalOpen={setIsMapOrModalOpen}
                            isMapOrModalOpen={isMapOrModalOpen}
                            header='Featured Items'
                            orientation={ooConfig?.cardOrientation}
                            menuItems={popularItems}
                            menuConfig={{
                              format: ooFormatConfig,
                              colors: ooConfig?.colors
                            }}
                            itemSource={
                              isSearching ? ItemSource.Search : undefined
                            }
                            variableMenuSpacing={variableMenuSpacing}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className='sectionSeparatorWrapper flexible'>
                    <hr className='sectionSeparator' />
                  </div>
                </div>
                <div
                  className={classnames(
                    'menuSectionWrapper',
                    getTemplateClass(ooFormatConfig?.template),
                    { columns: ooFormatConfig?.columns }
                  )}
                  ref={pageNode}
                >
                  {refMenuPages?.length ? (
                    refMenuPages.map((page, pageIndex) => (
                      <div
                        role='tabpanel'
                        aria-labelledby={`${normalizeHtmlId(page.name)}-tab`}
                        className='paddedContentWrapper'
                        key={`${page.name}-${page.guid}`}
                        ref={page.ref}
                        tabIndex={-1}
                        id={`menu-${page.guid}`}
                      >
                        <div className='menu paddedContent'>
                          {shouldShowMenuTitles && (
                            <div className='headerWrapper'>
                              <MenuHeader
                                header={page.name}
                                badge={
                                  (menuOffers[page.guid]?.length ?? 0) > 0 ? (
                                    <OfferBadge
                                      text={
                                        (menuOffers[page.guid]?.length ?? 0) > 1
                                          ? 'OFFERS'
                                          : 'OFFER'
                                      }
                                    />
                                  ) : null
                                }
                              />
                            </div>
                          )}
                          <div className='menuGroupsContainer'>
                            {page.groups.map((group) => (
                              <MenuSection
                                key={group.guid}
                                restaurantGuid={restaurantGuid}
                                setIsMapOrModalOpen={setIsMapOrModalOpen}
                                isMapOrModalOpen={isMapOrModalOpen}
                                onVisible={() => {
                                  const newSection = group.guid
                                  if (scrolledGuid === selectedGuid) {
                                    setSelectedGuid(newSection)
                                  }
                                  if (selectedMenuIndex === scrolledMenuIndex) {
                                    setSelectedMenuIndex(pageIndex)
                                    setSelectedGuid(newSection)
                                  }
                                  setScrolledGuid(newSection)
                                  setScrolledMenuIndex(pageIndex)
                                }}
                                ref={group.ref}
                                groupGuid={group.guid}
                                menuGuid={page.guid}
                                header={
                                  page.groups.length < 2 &&
                                  group.name.toLowerCase() ===
                                    page.name.toLowerCase()
                                    ? undefined
                                    : group.name
                                }
                                description={group.description}
                                orientation={ooConfig?.cardOrientation}
                                menuItems={group.items}
                                menuConfig={{
                                  format: ooFormatConfig,
                                  colors: ooConfig?.colors
                                }}
                                itemSource={
                                  isSearching ? ItemSource.Search : undefined
                                }
                                variableMenuSpacing={variableMenuSpacing}
                              />
                            ))}
                          </div>
                          {pageIndex !== refMenuPages.length - 1 ? (
                            <hr className='sectionSeparator' />
                          ) : null}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className='paddedSection'>
                      <MenuSection onVisible={() => {}} />
                    </div>
                  )}
                  {menuItems?.length && groupings ? (
                    <PaginatedMenu
                      menuItems={menuItems}
                      loading={loading || false}
                      fetchMore={fetchMore}
                      loadingNewFilterData={loadingNewFilterData || false}
                      menuConfig={{
                        format: ooConfig?.format,
                        colors: ooConfig?.colors
                      }}
                      hideOutOfStockItems={ooConfig?.hideOutOfStockItems}
                      variableMenuSpacing={variableMenuSpacing}
                    />
                  ) : null}
                  {!loading && !menuItems?.length && !refMenuPages?.length ? (
                    <NoAvailableMenuItems />
                  ) : null}
                  {shouldShowSurchargeDisclaimer && (
                    <div className='paddedSection'>
                      <CreditCardSurchargeDisclaimer
                        className='footerStyleDisclaimer'
                        surcharges={surcharges}
                        restaurantName={getDisplayBusinessName(
                          restaurant?.name,
                          selectedLocation?.name
                        )}
                        surchargingConfig={surchargingConfig}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {isMobile && <StickyOrderFooter />}
          </div>
        )}
      </div>
      <OrderPageSchema
        restaurant={restaurant}
        site={siteRestaurant}
        selectedLocation={selectedLocation}
        locationSchedule={locationSchedule}
        menus={menus}
      />
      <DeliveryModal
        isOpen={deliveryModalOpen}
        onClose={() => setDeliveryModalOpen(false)}
        forceScreenPlacement={deliveryModalForcedScreen}
        returnFocusElementId='delivery-address-summary-button'
      />
      {/* Only necessary when the currently set schedule time has expired or delivery is selected without an address */}
      <FulfillmentTimeModal returnFocusElementId='fulfillment-time-summary-button' />
      {/* Only necessary when the guest has attempted to add an item to the cart that conflicts with the cart's current selections */}
      <ItemResolutionModal />
      {deeplinkItem && (
        <DeeplinkItemModal key={deeplinkItem.guid} item={deeplinkItem} />
      )}
      {isEditor ? null : <UpsellModal />}
      {isEditor ? null : <QRCodeLandingModal />}
    </>
  )
}

/**
 * Inner component that actually performs tracking - only used in non-editor mode
 * This avoids calling hooks that require OO context providers in the editor
 */
const TrackingOrderPageInner = ({ children }: React.PropsWithChildren<{}>) => {
  const { toastProduct } = useRestaurant()

  const { historySet } = useNavHistory()
  const tracker = useTracker()

  // update amplitude referrer to toast website for attribution
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      toastProduct === ToastProduct.Sites &&
      historySet.find(isWebsiteNonOrderRoute)
    ) {
      const [referrer, referringDomain] = getReferrerProperties(
        window.location.origin
      )
      referrer &&
        referringDomain &&
        tracker.register({ referrer, referring_domain: referringDomain })
    }
  }, [toastProduct, tracker, historySet])

  // This hook requires TimeBasedRuleContext and other OO contexts
  useTrackOrderPage()

  return <>{children}</>
}

/**
 * Wrapper that conditionally uses tracking based on editor mode
 * In editor mode, skips all tracking to avoid requiring OO context providers
 */
export const TrackingOrderPage = ({
  children
}: React.PropsWithChildren<{}>) => {
  const { isEditor } = useEditor()

  // In editor mode, skip tracking entirely to avoid context provider dependencies
  if (isEditor) {
    return <>{children}</>
  }

  return <TrackingOrderPageInner>{children}</TrackingOrderPageInner>
}

const OrderPage = () => {
  return (
    <OrderPageWrapper channel={Channel.OO}>
      {(props) => (
        <TrackingOrderPage>
          <OrderPageContent {...props} />
        </TrackingOrderPage>
      )}
    </OrderPageWrapper>
  )
}
export default React.memo(OrderPage)
